import classNames from 'classnames';
import Image from '@/shared/components/shared/Image';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { ImageShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { localAssetUrl } from '@/shared/lib/utils/api-asset-url';

type BlockquoteSectionItem = {
    title?: string | null;
    text?: string | null;
    img?: ImageShape | null;
};

export type RawProps = {
    quote?: string | null;
    authorName?: string | null;
    authorJob?: string | null;
    authorImage?: ImageShape | null;
    items?: BlockquoteSectionItem[] | null;
};

type Props = React.HTMLAttributes<HTMLElement> & RawProps;

function BlockquoteSectionItemCard({ title, text, img }: BlockquoteSectionItem) {
    const tpTitle = useTypograf(title ?? '');
    const hasText = Boolean(text);

    if (!tpTitle && !text && !img?.src) {
        return null;
    }

    return (
        <li className="blockquote-section__item">
            <div className="blockquote-section__item-main">
                {tpTitle && <div className="blockquote-section__item-title text-xl">{tpTitle}</div>}
                {hasText && (
                    <div
                        className="blockquote-section__item-text text-m wysiwyg"
                        dangerouslySetInnerHTML={{ __html: tp(text ?? '') }}
                    />
                )}
            </div>
            {img?.src && (
                <div
                    className="blockquote-section__item-img"
                    style={{ ['--blockquote-item-img' as string]: `url(${localAssetUrl(img.src)})` }}
                    aria-hidden
                />
            )}
        </li>
    );
}

function BlockquoteSection({ quote, authorName, authorJob, authorImage, items, className, ...props }: Props) {
    const tpQuote = useTypograf(quote ?? '');
    const tpAuthorName = useTypograf(authorName ?? '');
    const tpAuthorJob = useTypograf(authorJob ?? '');
    const filteredItems = items?.filter((item) => item.title || item.text || item.img?.src) ?? [];
    const hasAuthor = Boolean(tpAuthorName || tpAuthorJob || authorImage?.src);
    const hasQuote = Boolean(tpQuote);
    const hasItems = filteredItems.length > 0;

    if (!hasAuthor && !hasQuote && !hasItems) {
        return null;
    }

    return (
        <section
            {...props}
            className={classNames('blockquote-section js-header-theme-trigger', className)}
            data-theme="dark"
        >
            <div className="wrapper blockquote-section__wrapper">
                {(hasAuthor || hasQuote) && (
                    <div className="blockquote-section__top">
                        {hasAuthor && (
                            <div className="blockquote-section__author">
                                {authorImage?.src && (
                                    <div className="blockquote-section__author-img">
                                        <Image
                                            className="blockquote-section__author-img-inner"
                                            src={authorImage.src}
                                            alt={authorImage.alt || tpAuthorName || ''}
                                            title={authorImage.title}
                                            width={authorImage.width}
                                            height={authorImage.height}
                                            sizes="140px"
                                        />
                                    </div>
                                )}
                                {(tpAuthorName || tpAuthorJob) && (
                                    <div className="blockquote-section__author-info">
                                        {tpAuthorName && (
                                            <div className="blockquote-section__author-name text-xl">
                                                {tpAuthorName}
                                            </div>
                                        )}
                                        {tpAuthorJob && (
                                            <div className="blockquote-section__author-job text-m">{tpAuthorJob}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {hasQuote && (
                            <blockquote className="blockquote-section__quote">
                                <span className="blockquote-section__quote-mark h3" aria-hidden="true">
                                    “
                                </span>
                                <div
                                    className="blockquote-section__quote-text text-l"
                                    dangerouslySetInnerHTML={{ __html: tpQuote }}
                                />
                            </blockquote>
                        )}
                    </div>
                )}

                {hasItems && (
                    <ul className="blockquote-section__list list-unstyled">
                        {filteredItems.map((item, index) => (
                            <BlockquoteSectionItemCard key={index} {...item} />
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

export default BlockquoteSection;
